/**
 * Austrian rule sets per calendar year.
 *
 * Every figure carries its legal source in a comment. Values flagged in
 * `provisional` are the ones to re-check before relying on a filing — they are
 * either announced but not yet in force, or taken from a secondary source.
 *
 * Sources (retrieved 2026-08):
 *   - Einkommensteuertarif 2026: WKO "Aktuelle Werte: Einkommen-/Körperschaftsteuer 2026"
 *     (Inflationsanpassung +1,733 %, zwei Drittel der Inflationsrate von 2,6 %)
 *   - Kleinunternehmer: § 6 Abs 1 Z 27 UStG idF ab 1.1.2025, USP.gv.at
 *   - Gewinnfreibetrag: § 10 EStG, WKO
 *   - Kilometergeld/Diäten: BMF, gültig ab 1.7.2025
 *   - SVS: SVS Beitragstabelle 2026, WKO "Beitragswesen Selbständige 2026"
 *   - Basispauschalierung: § 17 Abs 1 und Abs 2 Z 2 EStG idF BBG 2025 (BGBl I 25/2025)
 *   - Investitionsfreibetrag, befristete Erhöhung: § 124b Z 480 EStG (BGBl I 79/2025)
 */
import { toCents } from '../../money.js';
import { datenstrom } from './datenstrom.js';
import { e1aForm } from './e1a.js';
import { taxCodes } from './taxCodes.js';
import { uvaForm } from './uva.js';
/**
 * Erfahrungswerte der Finanzverwaltung. These prefill the asset form; the user
 * can always override, since § 7 EStG asks for the *betriebsgewöhnliche*
 * Nutzungsdauer of the concrete asset.
 */
const USEFUL_LIVES = [
    { key: 'computer', label: 'Computer, Notebook, Server', years: 3 },
    { key: 'smartphone', label: 'Smartphone, Tablet', years: 3 },
    { key: 'peripherals', label: 'Monitor, Drucker, Netzwerk', years: 3 },
    { key: 'software', label: 'Software (Einzelplatzlizenz)', years: 3 },
    { key: 'furniture', label: 'Büromöbel', years: 10 },
    { key: 'office_equipment', label: 'Büromaschinen, Kaffeemaschine', years: 5 },
    { key: 'machines', label: 'Maschinen, Werkzeuge', years: 8 },
    { key: 'car', label: 'PKW und Kombi', years: 8, hint: 'Mindestnutzungsdauer laut § 8 Abs 6 EStG' },
    { key: 'truck', label: 'LKW, Klein-LKW', years: 5 },
    { key: 'bicycle', label: 'Fahrrad, E-Bike', years: 5 },
    { key: 'photovoltaic', label: 'Photovoltaikanlage', years: 20 },
    {
        key: 'building_business',
        label: 'Betriebsgebäude',
        years: 40,
        hint: 'Entspricht 2,5 % AfA laut § 8 Abs 1 EStG',
    },
    {
        key: 'building_residential',
        label: 'Gebäude für Wohnzwecke',
        years: 67,
        hint: 'Entspricht 1,5 % AfA laut § 8 Abs 1 EStG',
    },
    { key: 'fitout', label: 'Mieterinvestition, Einbauten', years: 10 },
];
/** § 7, § 8, § 13 EStG. Stable across 2024–2026 — only the IFB moves per year. */
const AFA_BASE = {
    // § 13 EStG: 1.000 Euro seit 2023 (davor 800 Euro).
    gwgLimit: toCents(1000),
    // § 7 Abs 2 EStG: mehr als sechs Monate in Verwendung -> volle Jahres-AfA.
    halfYearRule: true,
    degressive: {
        // § 7 Abs 1a EStG, unbefristet für Anschaffungen ab 1.7.2020.
        available: true,
        maxRate: 0.3,
        excludedKinds: ['building', 'vehicle', 'intangible'],
        switchToLinearAllowed: true,
    },
    buildings: {
        businessRate: 0.025,
        residentialRate: 0.015,
        // § 8 Abs 1a EStG: dreifacher bzw. doppelter Satz in den ersten beiden Jahren.
        accelerated: { available: true, firstYearFactor: 3, secondYearFactor: 2 },
    },
    car: {
        // § 8 Abs 6 EStG.
        minUsefulLifeYears: 8,
        // Angemessenheitsgrenze laut PKW-AngemessenheitsVO, BGBl II 466/2004:
        // 40.000 Euro brutto, seither unverändert und nicht valorisiert.
        luxuryLimit: toCents(40_000),
        // EStR 2000 Rz 4776: innerhalb von fünf Jahren nach der Erstzulassung
        // zählt der seinerzeitige Neupreis, danach der Kaufpreis.
        usedCarListPriceMonths: 60,
        electricFullInputVatLimit: toCents(40_000),
        electricNoInputVatLimit: toCents(80_000),
    },
};
const IFB_PERMANENT = {
    // § 11 EStG, wieder eingeführt für Anschaffungen ab 1.1.2023.
    available: true,
    baseRate: 0.1,
    ecoRate: 0.15,
    // § 11 Abs 3 Z 2 EStG: PKW und Kombi bleiben ausgenommen, Fahrzeuge mit
    // einem CO2-Emissionswert von 0 g/km ausdrücklich nicht. Z 3 nimmt Gebäude
    // und Herstellungsaufwendungen auf ein Gebäude dazu.
    excludedKinds: ['vehicle', 'building'],
    // Öko-IFB-VO, BGBl II 155/2023: Elektrofahrzeuge zählen zur Ökologisierung.
    ecoKinds: ['electric_vehicle'],
    // § 11 Abs 3 Z 2 EStG — Wirtschaftsgüter mit einer betriebsgewöhnlichen
    // Nutzungsdauer von weniger als vier Jahren.
    minUsefulLifeYears: 4,
    // Z 5: geringwertige Wirtschaftsgüter, wenn sie sofort abgesetzt werden.
    excludesGwg: true,
    // Z 6: gebrauchte Wirtschaftsgüter.
    excludesUsed: true,
    // Z 1: nicht neben dem investitionsbedingten Gewinnfreibetrag auf
    // derselben Anlage.
    excludesGfbAssets: true,
    maxBasePerYear: toCents(1_000_000),
    holdingPeriodYears: 4,
};
const AFA_2024 = { ...AFA_BASE, ifb: IFB_PERMANENT };
/**
 * § 124b Z 480 EStG (BGBl I 79/2025): für Anschaffungs- und Herstellungskosten,
 * die nach dem 31.10.2025 und vor dem 1.1.2027 anfallen, beträgt der
 * Investitionsfreibetrag 20 % bzw. ökologisch 22 %. Die Dauersätze bleiben
 * daneben stehen, und das Fenster hängt als Datum am Jahr — ein späteres Jahr,
 * das mangels eigenem Regelwerk auf 2026 geklemmt wird, rechnet dadurch nicht
 * stillschweigend mit den erhöhten Sätzen weiter.
 */
const AFA_2025 = {
    ...AFA_BASE,
    ifb: {
        ...IFB_PERMANENT,
        elevated: {
            validFrom: '2025-11-01',
            validUntil: '2025-12-31',
            baseRate: 0.2,
            ecoRate: 0.22,
            // Der Höchstbetrag von 1.000.000 Euro gilt aliquot für die zwei Monate
            // des Fensters im Kalenderjahr 2025: 2/12, kaufmännisch auf den Cent.
            maxBase: toCents(166_666.67),
        },
    },
};
const AFA_2026 = {
    ...AFA_BASE,
    ifb: {
        ...IFB_PERMANENT,
        elevated: {
            // Das ganze Kalenderjahr 2026 liegt im Fenster des § 124b Z 480 EStG.
            validFrom: '2026-01-01',
            validUntil: '2026-12-31',
            baseRate: 0.2,
            ecoRate: 0.22,
            maxBase: toCents(1_000_000),
        },
    },
};
/** § 10 EStG — identisch 2024 bis 2026. */
const GEWINNFREIBETRAG = {
    grundfreibetragRate: 0.15,
    grundfreibetragBase: toCents(33_000),
    tiers: [
        { upTo: toCents(33_000), rate: 0.15 },
        { upTo: toCents(178_000), rate: 0.13 },
        { upTo: toCents(353_000), rate: 0.07 },
        { upTo: toCents(583_000), rate: 0.045 },
        { upTo: null, rate: 0 },
    ],
    maxTotal: toCents(46_400),
    // § 10 Abs 3 EStG: abnutzbare körperliche Wirtschaftsgüter mit einer
    // betriebsgewöhnlichen Nutzungsdauer von mindestens vier Jahren.
    minUsefulLifeYears: 4,
    holdingPeriodYears: 4,
};
/**
 * Reisekosten laut BMF, Sätze gültig ab 1.7.2025. Das PKW-Kilometergeld von
 * 0,50 Euro gilt schon seit 1.1.2025; Motorrad und Fahrrad standen im ersten
 * Halbjahr 2025 ebenfalls auf 0,50 Euro und erst ab 1.7.2025 auf 0,25.
 * Hinterlegt ist ein Satz je Jahr — für Jänner bis Juni 2025 sind die
 * Vorschlagswerte für Motorrad und Fahrrad daher zu niedrig; der Betrag lässt
 * sich je Beleg überschreiben (siehe STEUERRECHT.md).
 */
const TRAVEL_FROM_2025_07 = {
    mileageCar: toCents(0.5),
    mileageMotorcycle: toCents(0.25),
    mileageBicycle: toCents(0.25),
    mileagePassenger: toCents(0.15),
    maxKilometresPerYear: 30_000,
    // Fahrrad: höchstens 3.000 km im Jahr seit 2025 (oesterreich.gv.at,
    // Seite 610700, Stand 13.03.2025 — dort als Deckel von 1.500 € bei 0,50 €/km).
    maxKilometresPerYearBicycle: 3_000,
    perDiemDomestic: toCents(30),
    overnightDomestic: toCents(17),
    minTripHours: 3,
    twelfthsPerHour: true,
};
/**
 * § 17 Abs 1 und Abs 2 Z 2 EStG. Das Budgetbegleitgesetz 2025 (BGBl I 25/2025)
 * hebt die Basispauschalierung in zwei Stufen an: für 2025 auf 320.000 Euro
 * Vorjahresumsatz und 13,5 %, ab 2026 auf 420.000 Euro und 15 %. Der Satz von
 * 6 % für Tätigkeiten nach § 22 Z 2 EStG und für schriftstellerische,
 * vortragende, wissenschaftliche, unterrichtende oder erzieherische Arbeit
 * bleibt unverändert; jeder Höchstbetrag ist Satz mal Umsatzgrenze.
 */
const BASIS_PAUSCHALIERUNG_2024 = {
    available: true,
    turnoverLimit: toCents(220_000),
    standardRate: 0.12,
    reducedRate: 0.06,
    maxDeduction: toCents(26_400),
    reducedMaxDeduction: toCents(13_200),
    // § 17 Abs 1 EStG: Wareneingang (9100), Fremdlöhne (9110), Löhne samt
    // Lohnnebenkosten (9120), Pflichtversicherungsbeiträge (9225) und das
    // Arbeitsplatzpauschale samt ergonomischem Mobiliar (9215/9217/9216).
    additionallyDeductibleKz: ['9100', '9110', '9120', '9225', '9215', '9216', '9217'],
    bindingYearsAfterOptOut: 5,
};
const BASIS_PAUSCHALIERUNG_2025 = {
    ...BASIS_PAUSCHALIERUNG_2024,
    turnoverLimit: toCents(320_000),
    standardRate: 0.135,
    maxDeduction: toCents(43_200),
    reducedMaxDeduction: toCents(19_200),
};
const BASIS_PAUSCHALIERUNG_2026 = {
    ...BASIS_PAUSCHALIERUNG_2024,
    turnoverLimit: toCents(420_000),
    standardRate: 0.15,
    maxDeduction: toCents(63_000),
    reducedMaxDeduction: toCents(25_200),
};
// § 17 Abs 3a EStG, an die USt-Kleinunternehmergrenze gekoppelt; die
// Höchstbeträge stehen in Z 4 (45 % bzw. 20 % für Dienstleistungsbetriebe).
const KLEINUNTERNEHMER_PAUSCHALIERUNG_2025_2026 = {
    available: true,
    turnoverLimit: toCents(55_000),
    productionRate: 0.45,
    serviceRate: 0.2,
    maxDeduction: toCents(24_750),
    serviceMaxDeduction: toCents(11_000),
    // § 17 Abs 3a Z 3 EStG: Pflichtversicherungsbeiträge (9225) und das
    // Arbeitsplatzpauschale samt ergonomischem Mobiliar (9215/9217/9216).
    additionallyDeductibleKz: ['9225', '9215', '9216', '9217'],
};
/**
 * § 4 Abs 4 Z 8 EStG. Der Grenzbetrag für die anderen aktiven Einkünfte wird
 * jährlich valorisiert und entspricht der ersten Tarifstufe des Jahres
 * (USP „Arbeitsplatzpauschale": 12.816 für 2024, 13.308 für 2025, 13.539
 * für 2026 laut BGBl II 191/2025); `taxlaw.test.ts` hält die Gleichheit fest.
 * Das große Pauschale setzt beides voraus: andere aktive Einkünfte höchstens
 * bis zum Grenzbetrag *und* kein anderer Raum, der zur Verfügung steht.
 *
 * Abgeschafft für Wirtschaftsjahre, die nach dem 31.12.2026 beginnen
 * (§ 124b Z 495 EStG, Budgetbegleitgesetz 2027–2028, BGBl I 62/2026) — ein
 * künftiges Regelwerk 2027 darf das Pauschale nicht mehr enthalten.
 */
function arbeitsplatzpauschale(otherIncomeLimitEuros) {
    return {
        full: toCents(1200),
        reduced: toCents(300),
        otherIncomeLimit: toCents(otherIncomeLimitEuros),
    };
}
const PAUSCHALIERUNG_2025 = {
    basis: BASIS_PAUSCHALIERUNG_2025,
    kleinunternehmer: KLEINUNTERNEHMER_PAUSCHALIERUNG_2025_2026,
    arbeitsplatzpauschale: arbeitsplatzpauschale(13_308),
};
const PAUSCHALIERUNG_2026 = {
    basis: BASIS_PAUSCHALIERUNG_2026,
    kleinunternehmer: KLEINUNTERNEHMER_PAUSCHALIERUNG_2025_2026,
    arbeitsplatzpauschale: arbeitsplatzpauschale(13_539),
};
const SVS_2026 = {
    pensionRate: 0.185,
    healthRate: 0.068,
    provisionRate: 0.0153,
    // WKO „Beitragswesen Selbständige 2026": 12,95 € monatlich, 155,40 € im Jahr.
    accidentInsuranceMonthly: toCents(12.95),
    minContributionBaseMonthly: toCents(551.1),
    maxContributionBaseMonthly: toCents(8085),
    smallBusinessExemptionProfit: toCents(6613.2),
    smallBusinessExemptionTurnover: toCents(55_000),
};
/**
 * Anstellung neben dem Unternehmen. Die ASVG-Höchstbeitragsgrundlage hängt mit
 * der GSVG-Grundlage zusammen: zwölf GSVG-Monate entsprechen vierzehn
 * ASVG-Monaten (laufende Bezüge plus zwei Sonderzahlungsgrundlagen, § 35b
 * GSVG); `taxlaw.test.ts` hält die Gleichung fest.
 *
 * Dienstnehmeranteile für Angestellte und Arbeiter: KV 3,87 % + PV 10,25 % +
 * ALV 2,95 % + AK-Umlage 0,5 % + Wohnbauförderungsbeitrag 0,5 % = 18,07 % auf
 * laufende Bezüge; Sonderzahlungen ohne AK-Umlage und WF-Beitrag = 17,07 %
 * (WKO „Sozialversicherungswerte", unverändert 2024–2026).
 */
const EMPLOYMENT_RATES = {
    employeeRateOngoing: 0.1807,
    employeeRateSpecial: 0.1707,
    // § 16 Abs 3 EStG, seit Jahrzehnten unverändert.
    werbungskostenPauschale: toCents(132),
    // § 39 Abs 5 EStG (bis 2023 § 41 Abs 3, verschoben durch das AbgÄG 2024,
    // BGBl I 113/2024), nicht valorisiert.
    veranlagungsfreibetrag: toCents(730),
};
// ASVG monatliche Höchstbeitragsgrundlage: 6.060 € (2024), 6.450 € (2025),
// 6.930 € (2026); Sonderzahlungsdeckel je das Doppelte (§ 54 Abs 1 ASVG).
const EMPLOYMENT_2024 = {
    ...EMPLOYMENT_RATES,
    asvgMaxBaseMonthly: toCents(6060),
    asvgMaxBaseSpecialAnnual: toCents(12_120),
};
const EMPLOYMENT_2025 = {
    ...EMPLOYMENT_RATES,
    asvgMaxBaseMonthly: toCents(6450),
    asvgMaxBaseSpecialAnnual: toCents(12_900),
};
const EMPLOYMENT_2026 = {
    ...EMPLOYMENT_RATES,
    asvgMaxBaseMonthly: toCents(6930),
    asvgMaxBaseSpecialAnnual: toCents(13_860),
};
/** § 6 Abs 1 Z 27 UStG in der Fassung ab 1.1.2025 (EU-Kleinunternehmerreform). */
const VAT_2025 = {
    standardRate: 0.2,
    reducedRates: [0.13, 0.1],
    // Die fünf Betragsfelder des maschinenlesbaren Codes. Welche Sätze
    // dahinterstehen, entscheidet das Jahr: Das Regelwerk 2026 ordnet dem Feld
    // „Betrag-Satz-Besonders" ab 1.7.2026 den neuen 4,9-%-Satz zu — siehe dort.
    registrierkasse: {
        normalRate: 0.2,
        reduced1Rate: 0.1,
        reduced2Rate: 0.13,
        zeroRate: 0,
        specialRate: 0.19,
    },
    kleinunternehmer: {
        threshold: toCents(55_000),
        // Überschreiten um bis zu 10 % lässt die Befreiung bis Jahresende bestehen.
        toleranceRate: 0.1,
        basis: 'net',
        waiverBindingYears: 5,
    },
    uva: {
        monthlyThreshold: toCents(100_000),
        // Seit der Kleinunternehmerreform 2025 folgt die Befreiung von der
        // Voranmeldung der 55.000-Euro-Grenze (§ 21 UStG iVm § 6 Abs 1 Z 27 UStG).
        noFilingThreshold: toCents(55_000),
        dueDayOfSecondFollowingMonth: 15,
    },
    acquisitionThreshold: toCents(11_000),
    distanceSellingThreshold: toCents(10_000),
    // § 11 Abs 6 UStG, unverändert seit 2014.
    smallInvoiceLimit: toCents(400),
    // § 11 Abs 1 Z 2 UStG.
    customerVatIdRequiredAbove: toCents(10_000),
    invoiceDeadlineMonths: 6,
};
/**
 * Ab 1.7.2026 kommt der dritte ermäßigte Satz von 4,9 % für ausgewählte
 * Grundnahrungsmittel dazu (§ 10 Abs 1a UStG iVm Anlage 3, BGBl I 37/2026).
 * Alles andere ist unverändert.
 */
const VAT_2026 = {
    ...VAT_2025,
    reducedRates: [0.13, 0.1, 0.049],
    registrierkasse: {
        ...VAT_2025.registrierkasse,
        // BMF-Fachinformation zur USt-Senkung auf ausgewählte Nahrungsmittel iVm
        // der RKSV-Novelle vom März 2026: ab 1.7.2026 trägt „Betrag-Satz-
        // Besonders" den Satz von 4,9 %; davor stand dort der 19-%-Satz für
        // Jungholz und Mittelberg.
        specialRateFrom: { validFrom: '2026-07-01', rate: 0.049 },
    },
};
/** § 132 BAO. Bis auf die Kalte-Hände-Grenze seit Jahren unverändert. */
const BAO_UNTIL_2025 = {
    // § 132 Abs 1 BAO: sieben Jahre.
    retentionYears: 7,
    // § 18 Abs 10 UStG: 22 Jahre für Aufzeichnungen und Unterlagen, die
    // Grundstücke betreffen — der Vorsteuerberichtigungszeitraum des
    // § 12 Abs 10 UStG dauert 20 Jahre, die Aufbewahrung reicht zwei darüber.
    retentionYearsGrundstuecke: 22,
    // § 132 Abs 1 BAO: „ab Schluss des Kalenderjahres, für das die Eintragungen
    // vorgenommen worden sind".
    retentionStartsAtEndOfYear: true,
    // § 132 Abs 2 BAO: Aufbewahrung auf Datenträgern nur, wenn die vollständige,
    // geordnete und inhaltsgleiche Wiedergabe bis zum Fristende gesichert ist.
    machineReadableExportRequired: true,
    // § 131b Abs 1 Z 2 BAO iVm BarUV 2015: beide Grenzen zusammen, unverändert
    // seit 1.1.2016.
    registrierkasseTurnoverThreshold: toCents(15_000),
    registrierkasseCashThreshold: toCents(7_500),
    // § 131 Abs 4 Z 1 BAO iVm BarUV 2015: vereinfachte Losungsermittlung für
    // Umsätze im Freien, Hütten, Buschenschank und kleine Vereinskantinen —
    // 30.000 Euro bis einschließlich 2025.
    kalteHaendeTurnoverThreshold: toCents(30_000),
    // § 189 Abs 1 Z 2 UGB iVm § 124 BAO.
    buchfuehrungspflichtTurnoverThreshold: toCents(700_000),
};
/**
 * BGBl II 321/2025 (Novelle der BarUV 2015, in Kraft 1.1.2026) hebt die
 * Kalte-Hände-Grenze des § 131 Abs 4 Z 1 BAO auf 45.000 Euro pro Kalenderjahr.
 */
const BAO_2026 = {
    ...BAO_UNTIL_2025,
    kalteHaendeTurnoverThreshold: toCents(45_000),
};
function base(year, afa, bao) {
    return {
        year,
        country: 'AT',
        bao,
        taxCodes: taxCodes(year),
        uvaForm: uvaForm(year),
        e1aForm: e1aForm(year),
        datenstrom: datenstrom(year),
        zmForm: { formId: 'U13', dueDayOfFollowingMonth: 31 },
        afa,
        usefulLives: USEFUL_LIVES,
        travel: TRAVEL_FROM_2025_07,
    };
}
const YEAR_2026 = {
    ...base(2026, AFA_2026, BAO_2026),
    vat: VAT_2026,
    incomeTax: {
        // Tarifstufen 2026 nach Inflationsanpassung (+1,733 %).
        brackets: [
            { upTo: toCents(13_539), rate: 0 },
            { upTo: toCents(21_992), rate: 0.2 },
            { upTo: toCents(36_458), rate: 0.3 },
            { upTo: toCents(70_365), rate: 0.4 },
            { upTo: toCents(104_859), rate: 0.48 },
            { upTo: toCents(1_000_000), rate: 0.5 },
            { upTo: null, rate: 0.55 },
        ],
        gewinnfreibetrag: GEWINNFREIBETRAG,
        // § 45 EStG.
        vorauszahlungThreshold: toCents(300),
        vorauszahlungUpliftFirstYear: 0.04,
        vorauszahlungUpliftFurtherYears: 0.05,
    },
    svs: SVS_2026,
    employment: EMPLOYMENT_2026,
    pauschalierung: PAUSCHALIERUNG_2026,
};
const YEAR_2025 = {
    ...base(2025, AFA_2025, BAO_UNTIL_2025),
    vat: VAT_2025,
    incomeTax: {
        brackets: [
            { upTo: toCents(13_308), rate: 0 },
            { upTo: toCents(21_617), rate: 0.2 },
            { upTo: toCents(35_836), rate: 0.3 },
            { upTo: toCents(69_166), rate: 0.4 },
            { upTo: toCents(103_072), rate: 0.48 },
            { upTo: toCents(1_000_000), rate: 0.5 },
            { upTo: null, rate: 0.55 },
        ],
        gewinnfreibetrag: GEWINNFREIBETRAG,
        vorauszahlungThreshold: toCents(300),
        vorauszahlungUpliftFirstYear: 0.04,
        vorauszahlungUpliftFurtherYears: 0.05,
    },
    svs: {
        ...SVS_2026,
        // WKO „Beitragswesen Selbständige 2025": Unfallversicherung 12,07 €,
        // Mindestbeitragsgrundlage 551,10 €, Höchstbeitragsgrundlage 7.525 €.
        accidentInsuranceMonthly: toCents(12.07),
        minContributionBaseMonthly: toCents(551.1),
        maxContributionBaseMonthly: toCents(7525),
    },
    employment: EMPLOYMENT_2025,
    pauschalierung: PAUSCHALIERUNG_2025,
};
const YEAR_2024 = {
    ...base(2024, AFA_2024, BAO_UNTIL_2025),
    vat: {
        ...VAT_2025,
        kleinunternehmer: {
            // Alte Rechtslage: 35.000 Euro netto, einmalige 15-%-Toleranz in fünf Jahren.
            threshold: toCents(35_000),
            toleranceRate: 0.15,
            basis: 'net',
            waiverBindingYears: 5,
        },
        uva: {
            ...VAT_2025.uva,
            // Vor der Reform hing die Befreiung an der alten 35.000-Euro-Grenze.
            noFilingThreshold: toCents(35_000),
        },
    },
    incomeTax: {
        brackets: [
            { upTo: toCents(12_816), rate: 0 },
            { upTo: toCents(20_818), rate: 0.2 },
            { upTo: toCents(34_513), rate: 0.3 },
            { upTo: toCents(66_612), rate: 0.4 },
            { upTo: toCents(99_266), rate: 0.48 },
            { upTo: toCents(1_000_000), rate: 0.5 },
            { upTo: null, rate: 0.55 },
        ],
        gewinnfreibetrag: GEWINNFREIBETRAG,
        vorauszahlungThreshold: toCents(300),
        vorauszahlungUpliftFirstYear: 0.04,
        vorauszahlungUpliftFurtherYears: 0.05,
    },
    svs: {
        ...SVS_2026,
        // WKO „Beitragswesen Selbständige 2024": Unfallversicherung 11,35 €,
        // Mindestbeitragsgrundlage 518,44 €, Höchstbeitragsgrundlage 7.070 €;
        // Versicherungsgrenze der neuen Selbständigen 6.221,28 € (12 × 518,44).
        accidentInsuranceMonthly: toCents(11.35),
        minContributionBaseMonthly: toCents(518.44),
        maxContributionBaseMonthly: toCents(7070),
        smallBusinessExemptionProfit: toCents(6221.28),
        smallBusinessExemptionTurnover: toCents(35_000),
    },
    employment: EMPLOYMENT_2024,
    pauschalierung: {
        basis: BASIS_PAUSCHALIERUNG_2024,
        kleinunternehmer: {
            ...KLEINUNTERNEHMER_PAUSCHALIERUNG_2025_2026,
            // Alte Rechtslage: § 17 Abs 3a Z 2 EStG ließ die USt-Grenze von 35.000
            // um bis zu 5.000 Euro überschreiten, wirksam also 40.000; die
            // Höchstbeträge nennt Z 4 in der damaligen Fassung.
            turnoverLimit: toCents(40_000),
            maxDeduction: toCents(18_900),
            serviceMaxDeduction: toCents(8_400),
        },
        arbeitsplatzpauschale: arbeitsplatzpauschale(12_816),
    },
    travel: {
        ...TRAVEL_FROM_2025_07,
        // Sätze vor der Novelle zum 1.7.2025.
        mileageCar: toCents(0.42),
        mileageMotorcycle: toCents(0.24),
        mileageBicycle: toCents(0.38),
        // Bis 2024: höchstens 1.500 km (0,38 € × 1.500 = 570 € laut oesterreich.gv.at).
        maxKilometresPerYearBicycle: 1_500,
        perDiemDomestic: toCents(26.4),
        overnightDomestic: toCents(15),
    },
    // Tarifstufen und SVS-Werte 2024 sind gegen USP/BMF beziehungsweise die
    // „Beitragsrechtlichen Werte 2024" des Dachverbands geprüft (2026-08); nur
    // die Reisekostensätze stammen weiter aus zweiter Hand.
    provisional: ['travel'],
};
export const AT_TAX_YEARS = {
    2024: YEAR_2024,
    2025: YEAR_2025,
    2026: YEAR_2026,
};
export const AT_EARLIEST_YEAR = 2024;
export const AT_LATEST_YEAR = 2026;
//# sourceMappingURL=years.js.map